import { useMemo } from "react";

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
}

export const ProgressBar = ({ currentStep, totalSteps }: ProgressBarProps) => {
  const step = Math.max(1, Math.min(currentStep, totalSteps));
  const dotSize = 6;
  const pillWidth = 20;
  const pillHeight = 6;
  const pillRadius = 30;
  const dotRadius = 30;
  const gap = 8;
  const sidePadding = 9;
  // The pill's left offset: left padding + (step-1) * (dotSize + gap)
  const pillOffset = sidePadding + (step - 1) * (dotSize + gap);

  const steps = useMemo(
    () => Array.from({ length: totalSteps }, (_, i) => i + 1),
    [totalSteps],
  );
  // Container width: left padding + right padding + (dotSize * totalSteps) + (gap * (totalSteps - 1))
  const containerWidth =
    sidePadding * 2 + dotSize * totalSteps + gap * (totalSteps - 1);

  return (
    <div
      className="relative flex items-center"
      style={{
        width: containerWidth,
        height: pillHeight,
        paddingLeft: sidePadding,
        paddingRight: sidePadding,
      }}
      aria-label={`Progress: step ${step} of ${totalSteps}`}
    >
      {/* Dots (track) */}
      {steps.map(s => (
        <div
          key={s}
          className="bg-[#D2D7DB]"
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: dotRadius,
            marginRight: s !== totalSteps ? gap : 0,
          }}
        />
      ))}
      {/* Active pill */}
      <div
        className="absolute top-0 transition-transform duration-200"
        style={{
          width: pillWidth,
          height: pillHeight,
          borderRadius: pillRadius,
          left: pillOffset,
          background: "linear-gradient(90deg, #4F7EF4 0%, #4A2FFE 100%)",
          top: "50%",
          transform: "translateY(-50%)",
        }}
        aria-hidden="true"
      />
    </div>
  );
};
